import React, { useState } from 'react';
import { getImageUrl } from './uspUtils';

// Mobile-specific expanded USP modal UI (overlay card)
// Designed to match the provided mock: dark panel, circular previews, big zoom circle,
// info icon, Back/Next actions, and a center progress indicator.
const ExpandedUspModalMobile = ({
  onClose,
  onPrev,
  onNext,
  modalList,
  dropdownSelection,
  capitalize,
  currentItemType,
  isSpanishEnterprise,
  currentIndex = 1,
  totalItems = 1,
  combinedHotspotData = [],
  currentUspIndex = 0
}) => {
  const [showTooltip, setShowTooltip] = useState(false);
  
  const handleTooltipToggle = () => {
    setShowTooltip(prevState => !prevState);
  };

  const itemType = currentItemType || dropdownSelection;
  const imageUrl = getImageUrl(modalList, itemType);

  const getImageByIndex = (index) => {
    if (!combinedHotspotData || combinedHotspotData.length === 0) return imageUrl;
    const normalizedIndex = ((index % combinedHotspotData.length) + combinedHotspotData.length) % combinedHotspotData.length;
    const item = combinedHotspotData[normalizedIndex];
    return getImageUrl(item, item?.type);
  };

  const currentImageUrl = getImageByIndex(currentUspIndex) || imageUrl;
  const prevImageUrl = getImageByIndex(currentUspIndex - 1) || imageUrl;
  const nextImageUrl = getImageByIndex(currentUspIndex + 1) || imageUrl;

  // Title text based on item type and locale
  const titleText = itemType === 'EXT'
    ? (isSpanishEnterprise ? capitalize(modalList?.desc) : capitalize(modalList?.key))
    : modalList?.reasons?.[0]
      ? modalList?.reasons?.[0]
      : capitalize(modalList?.key);

  return (
    <div className="w-full h-full flex items-center justify-center p-3 sm:p-4">
      {/* Main modal container with flexbox */}
      <div className="relative w-full max-w-[300px] sm:max-w-[320px] h-full max-h-[85vh] min-h-[300px] landscape:min-h-[50vh] bg-[#222830] rounded-2xl overflow-hidden flex flex-col">
        
        {/* Header section - flexbox layout */}
        <div className="flex-shrink-0 flex items-start justify-between md:p-4 p-3 pb-2">
          <div className="flex-1">
            <h2 className="text-white text-sm sm:text-base font-semibold leading-tight w-[170px] truncate">
              {titleText}
            </h2>
          </div>
          
          {/* Info button container */}
          <div className="flex-shrink-0 flex gap-2">
            <button
              className={`absolute top-20 md:top-28 right-8 z-[100] w-4 h-4 rounded-full border flex items-center justify-center cursor-pointer transition-colors duration-200 text-[10px] leading-none ${
                showTooltip 
                  ? 'border-white bg-white text-[#222830]' 
                  : 'border-[#FFFFFFE5] text-[#FFFFFFE5] hover:bg-white/20'
              }`}
              aria-label="Info"
              onClick={handleTooltipToggle}
            >
              i
            </button>
            
            {/* Close button */}
            <button
              className="w-3 h-3 ml-1 rounded-full flex items-center justify-center bg-white/15 hover:bg-white/25 active:bg-white/30"
              onClick={onClose}
              aria-label="Close"
            >
              <img
                src={'https://spyne-static.s3.us-east-1.amazonaws.com/console/crossWhite.svg'}
                alt='Close'
                className="w-3 h-3 sm:w-4 sm:h-4"
              />
            </button>
          </div>
        </div>

        {/* Visuals area - flexbox layout */}
        <div className="flex-1 flex items-center justify-center px-4 md:py-12">
          {/* Main container for all visual elements */}
          <div className="relative flex items-center justify-center w-full max-w-[200px] h-full aspect-square">
            
            {/* Left column for preview circles */}
            <div className="absolute left-0 top-1/2 transform -translate-y-1/2 flex flex-col md:gap-32 gap-16">
              {/* Upper preview circle (previous) */}
              {prevImageUrl && (
                <div className="w-[40px] h-[40px] sm:w-[48px] sm:h-[48px] md:w-[48px] md:h-[48px] rounded-full overflow-hidden shadow-md flex-shrink-0">
                  <img src={prevImageUrl} alt="preview-upper" className="w-full h-full object-cover" />
                </div>
              )}
              
              {/* Lower preview circle (next) */}
              {nextImageUrl && (
                <div className="w-[40px] h-[40px] sm:w-[48px] sm:h-[48px] md:w-[56px] md:h-[56px] rounded-full overflow-hidden shadow-md flex-shrink-0">
                  <img src={nextImageUrl} alt="preview-lower" className="w-full h-full object-cover" />
                </div>
              )}
            </div>

            {/* Center main circular zoom area */}
            <div className="relative w-[140px] h-[140px] sm:w-[140px] sm:h-[140px] md:w-[140px] md:h-[140px] rounded-full flex-shrink-0">
              {/* Outer subtle ring */}
              <div className="absolute inset-[4px] rounded-full border border-white/40" />
              {/* Complete inner ring between outer ring and image */}
              <div className="absolute inset-[2px] rounded-full bg-white/10 backdrop-blur-sm" />
              {/* Image circle */}
              <div className="absolute inset-[12px] sm:inset-[16px] md:inset-[15px] rounded-full overflow-hidden shadow-[0_0_0_1px_rgba(255,255,255,0)]">
                <img src={currentImageUrl} alt="current-usp" className="w-full h-full object-cover" />
              </div>
            </div>
            
          </div>
        </div>

        {/* Footer actions - flexbox layout */}
        <div className="flex-shrink-0 flex items-center justify-between px-4 pb-3 min-h-[32px]">
          {/* Back button */}
          <div className="flex-1 flex justify-start">
            <button
              onClick={onPrev}
              className="text-[#FFC107] text-xs sm:text-sm font-semibold leading-tight py-1 px-2 min-w-[40px] text-left"
            >
              Back
            </button>
          </div>
          
          {/* Center progress indicator */}
          <div className="flex-shrink-0 flex items-center">
            <div className="min-w-[50px] sm:min-w-[60px] h-4 flex items-center justify-center rounded bg-[#1F2832] text-white text-xs sm:text-sm leading-tight px-3">
              {currentIndex}/{totalItems}
            </div>
          </div>
          
          {/* Next button */}
          <div className="flex-1 flex justify-end">
            <button
              onClick={onNext}
              className="text-[#FFC107] text-xs sm:text-sm font-semibold leading-tight py-1 px-2 min-w-[40px] text-right"
            >
              Next
            </button>
          </div>
        </div>
      </div>

      {/* Tooltip - Responsive positioning */}
      {showTooltip && (
        <div className="absolute left-60 top-24 md:top-28 w-[200px] sm:w-[220px] max-w-[calc(100vw-32px)] bg-white rounded-lg shadow-lg p-3 z-[200]">
          {/* Arrow pointing to the 'i' icon - positioned on the left */}
          <div className="absolute left-[-6px] top-4 w-0 h-0 border-t-[6px] border-b-[6px] border-r-[6px] border-t-transparent border-b-transparent border-r-white"></div>
          
          {/* Tooltip content - flexbox layout */}
          <div className="flex flex-col gap-2">
            <div className="flex justify-between items-start">
              <h3 className="text-[#000000CC] text-xs font-medium flex-1 pr-2 text-left">
                {capitalize(titleText) || 'No Heading Available'}
              </h3>
              <button 
                onClick={handleTooltipToggle}
                className="text-black text-sm font-normal hover:text-gray-600 flex-shrink-0 w-4 h-4 flex items-center justify-center"
              >
                ×
              </button>
            </div>
            <p className="text-[#00000066] text-[10px] font-normal leading-relaxed text-left">
              {modalList?.desc || modalList?.feature || modalList?.reasons?.[0] || 'No Description Available'}
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

export default ExpandedUspModalMobile;


